import React, { useEffect, useState } from 'react'
import { Container, Row, Col, Button, Modal, ToggleButtonGroup, ToggleButton } from 'react-bootstrap'
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from 'react-redux'
import { useAuth } from '../../../components/auth/useAuth'
import { addOperator, getOperators, getOperator, editOperator, removeOperator, disableOperator, enableOperator } from '../../../actions/operatorsActions'
import { CustomDataTable } from '../../../components/design/Datatables/CustomDataTable'
import Swal from 'sweetalert2'
import Loader from '../../../components/loaders/Loader'
import OperatorForm from '../../../components/forms/OperatorForm'

export default function Operators() {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const {clientauth} = useAuth()

    const {operators, operator, resultOperator, loading} = useSelector(state=>state.operators)
    const {parameters} = useSelector(state=>state.parameters)

    const [modalShow, setModalShow] = useState(false)
    const [formState, setFormState] = useState({})
    const [isRegistering, setIsRegistering] = useState(false)
    const [isAddingOrEditing, setIsAddingOrEditing] = useState(false)
    const [filterActive, setFilterActive] = useState(true)

    useEffect(() => {
        if(clientauth){
            dispatch(getOperators())
        }
    },[dispatch, t, clientauth])

    useEffect(()=>{
        if (operator) {
            setFormState(operator)
        }
    }, [operator, dispatch])

    const openModal = () =>{
        setIsAddingOrEditing('adding')
        setFormState({
            name: '',
            params: []
        })
        setModalShow(true)
    }

    const selectOperator = (operator) => {
        dispatch(getOperator(operator._id))
        setIsAddingOrEditing('editing')
        setFormState(operator)
        setModalShow(true)
    }

    const handleInputChange = ({target})=>{
        setFormState({
            ...formState,
            [target.name]: target.value
        })
    }

    const handleSubmit = async(e) => {
        e.preventDefault()
        setIsRegistering(true)

        const newFormState = {
            ...formState,
            params: formState.params || []
        }

        if(isAddingOrEditing==='editing'){
            dispatch(editOperator(newFormState, newFormState._id));
        }else{
            dispatch(addOperator(newFormState));
        }
        setIsRegistering(false)
    }

    const handleDeleteOperators = async(e, operatorId) => {
        e.preventDefault()
        Swal.fire({
            showConfirmButton: true,
            showCancelButton: true,
            confirmButtonText: t('global.accept'),
            cancelButtonText: t('global.cancel'),
            icon: 'warning',
            html: t('admin.operators.areyousureremove')
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(removeOperator(operatorId))
                setModalShow(false)
            }
        })
    }
    
    const disableEnableOperators = async(e) => {
        e.preventDefault()
        let textQuestion = ''
        formState.active
            ?
                textQuestion = t('admin.operators.areyousuredisable')+'<br><p><label className="fs-20 fw-600 pt-5">'+formState?.name+'</label></p>'
            :
                textQuestion = t('admin.operators.areyousureenable')+'<br><p><label className="fs-20 fw-600 pt-5">'+formState?.name+'</label></p>'

        Swal.fire({
            showConfirmButton: true,
            showCancelButton: true,
            confirmButtonText: t('global.accept'),
            cancelButtonText: t('global.cancel'),
            icon: 'question',
            html: textQuestion
        }).then((result) => {
            if (result.isConfirmed) {
                if (formState.active) {
                    dispatch(disableOperator(formState._id))
                }else{
                    dispatch(enableOperator(formState._id))
                }
            }
        })
    }

    useEffect(() => {
        if(resultOperator){
            let resultOperatorTemp = resultOperator
            dispatch({ type: 'OPERATOR_RESET_RESULT'})
            switch (resultOperatorTemp) {
                case 'addsuccess':
                    Swal.fire({
                        showConfirmButton: true,
                        showDenyButton: false,
                        confirmButtonText: t('global.great'),
                        icon: 'success',
                        text: t('admin.operators.addsuccess')
                    }).then(() => {
                        setModalShow(false)
                        setIsAddingOrEditing(false)
                    })
                    break
                case 'editsuccess':
                    Swal.fire({
                        showConfirmButton: true,
                        showDenyButton: false,
                        confirmButtonText: t('global.great'),
                        icon: 'success',
                        text: t('admin.operators.editsuccess')
                    }).then(() => {
                        setModalShow(false)
                        setIsAddingOrEditing(false)
                    })
                    break
                case 'deletesuccess':
                    Swal.fire({
                        showConfirmButton: true,
                        showDenyButton: false,
                        confirmButtonText: t('global.great'),
                        icon: 'success',
                        text: t('admin.operators.deletesuccess')
                    }).then(() => {
                        setModalShow(false)
                        setIsAddingOrEditing(false)
                    })
                    break
                case 'alreadyassigned':
                    Swal.fire({
                        showConfirmButton: true,
                        showDenyButton: false,
                        confirmButtonText: t('global.great'),
                        icon: 'warning',
                        text: t('admin.operators.alreadyassigned')
                    })
                    break
                default:
                    break
            }
        }
    // eslint-disable-next-line
    }, [resultOperator])

    const columnsOperators = [
        {
            name: t('admin.operators.name'),
            cell: operator =>
                <div className='cursor' onClick={() => selectOperator(operator)}>
                    {operator.name}
                </div>,
            selector: (operator) => `${operator.name}`,
            sortable: true,
        },{
            name: t('admin.operators.active'),
            width: '200px',
            cell: operator =>
                <div>
                    {operator.active
                        ?
                            <img src="../images/check.svg" alt="check" width={25}></img>
                        :
                            <img src="../images/times.svg" alt="times" width={25}></img>
                    }
                </div>,
            selector: (operator) => operator.active,
            sortable: true,
        },{
            name: '',
            width: '80px',
            cell: operator =>
                <div className='cursor' onClick={() => selectOperator(operator)}>
                    <i className='fas fa-pencil'></i>
                </div>,
            sortable: false,
        }
    ]

    const filteredOperators = operators?.filter(operator => operator.active === filterActive)

    return (
        <Container className='mt-5 px-0'>
            <Row>
                <Col xs={12} className='text-center'>
                    <h1>
                        {filterActive
                            ?
                            t('admin.operators.operatorsenabled')
                            :
                            t('admin.operators.operatorsdisabled')
                        }
                    </h1>
                </Col>
                <Col xs={6} className='d-flex justify-content-start'>
                    <ToggleButtonGroup
                        type="radio"
                        name="filterActive"
                        value={filterActive}
                        onChange={setFilterActive}
                    >
                        <ToggleButton id="tbg-active" value={true} variant={filterActive === true ? "primary" : "outline-secondary"}>
                            {t('admin.operators.enabled')}
                        </ToggleButton>
                        <ToggleButton id="tbg-inactive" value={false} variant={filterActive === false ? "primary" : "outline-secondary"}>
                            {t('admin.operators.disabled')}
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Col>
                <Col xs={12} className='d-flex justify-content-end'>
                    <Button variant='primary' type="submit" className="my-2 cursor" onClick={() => openModal()}>
                        {t('admin.operators.createoperator')}
                    </Button>
                </Col>
            </Row>

            {
                loading
                    ?
                        <Loader/>
                    :
                        <Row>
                            <Col xs={12}>
                            {operators?.length > 0
                                ?
                                    <CustomDataTable
                                        columns={columnsOperators}
                                        data={[...filteredOperators].sort((a, b) => a.name.localeCompare(b.name))}
                                        exportable={true}
                                        printable={false}
                                        filter={true}
                                    />
                                :
                                    <h4 className='mt-4 text-center'>{t('admin.operators.thereisnotoperators')}</h4>
                            }
                            </Col>
                        </Row>

            }
            <Modal
                show={modalShow}
                onHide={() => setModalShow(false)}
                aria-labelledby="contained-modal-title-vcenter"
                size="lg"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title id="contained-modal-title-vcenter">
                        <h2>{t('admin.operators.operators')}</h2>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <OperatorForm
                        state={formState}
                        handleInputChange={handleInputChange}
                        submit={handleSubmit}
                        isAddingOrEditing={isAddingOrEditing}
                        isRegistering={isRegistering}
                        handleDeleteOperators={handleDeleteOperators}
                        disableEnableOperators={disableEnableOperators}
                    />
                </Modal.Body>
            </Modal>
        </Container>
    )
}